import {
  updateDocument as updateDocumentAPI,
  confirmDocument as confirmDocumentAPI,
} from 'api/documents'
import { GLOBAL_PROFILE } from 'components/Root.loader'
import { StatusNo } from 'config'
import {
  NEW_STATUS_NO,
  EDITED_STATUS_NO,
  CHECKING_1_2_STATUS_NO,
  CHECKING_1_3_STATUS_NO,
  CHECKING_2_3_STATUS_NO,
  VERIFY_STATUS_NO,
  SEND_ERROR_STATUS_NO,
  CONFIRMED_STATUS_NO,
  SENT_STATUS_NO,
  DELETED_STATUS_NO,
} from 'config'
import { merge } from 'es-toolkit'

import { TDocInfo, VerifyData, DOC_TYPE } from './Documents'

export type ConfigItem = {
  display: boolean
  edit: boolean
  verify: boolean
}

export type Config = {
  [key: string]: ConfigItem
}

export interface BaseDocumentJSON {
  docInfo: TDocInfo
  ocrResp: unknown
  ocrEditResult: unknown
  config: unknown
  verifyData?: VerifyData
}

export function checkVerifyCsvDownloadable(statusNo: StatusNo) {
  /*
   - 登録担当者は、常にダウンロードができない
   - X: 一般ユーザーは、「確認待ち」「出力済み」でもダウンロードはできる
     O: 一般ユーザーは常にダウンロード可能
   - 運用管理者は、「出力待ち」の状態にならないとダウンロードができない
   - システム管理者は、「出力待ち」の状態にならないとダウンロードができない
  */
  return (
    GLOBAL_PROFILE.isRegister ||
    (GLOBAL_PROFILE.isManager && statusNo === CONFIRMED_STATUS_NO) ||
    (GLOBAL_PROFILE.isAdmin && statusNo === CONFIRMED_STATUS_NO) ||
    (GLOBAL_PROFILE.isOperator && false)
  )
}

export abstract class BaseDocument {
  abstract docJSON: BaseDocumentJSON

  get objectID() {
    return this.docInfo.objectID
  }
  get isInvoice() {
    return this.docInfo.documentType === DOC_TYPE.INVOICE
  }
  get isOrderForm() {
    return this.docInfo.documentType === DOC_TYPE.ORDER_FORM
  }
  get isOcrBuilder() {
    return this.docInfo.documentType === DOC_TYPE.OCR_BUILDER
  }
  get isGeneral() {
    return this.docInfo.documentType === DOC_TYPE.GENERAL_OCR
  }
  get docInfo() {
    return this.docJSON.docInfo
  }
  get isNew() {
    return this.documentStatusNo === NEW_STATUS_NO
  }
  get isEdited() {
    return this.documentStatusNo === EDITED_STATUS_NO
  }
  get isChecking() {
    return [
      CHECKING_1_2_STATUS_NO,
      CHECKING_1_3_STATUS_NO,
      CHECKING_2_3_STATUS_NO,
    ].includes(this.documentStatusNo)
  }
  get isSuccessfullyFinished() {
    return this.isConfirmed || this.isSent
  }
  get isFinished() {
    return this.isSendError || this.isSuccessfullyFinished
  }
  get isSent() {
    return this.documentStatusNo === SENT_STATUS_NO
  }
  get isConfirmed() {
    return this.documentStatusNo === CONFIRMED_STATUS_NO
  }
  get isSendError() {
    return this.documentStatusNo === SEND_ERROR_STATUS_NO
  }
  get isVerifying() {
    return this.documentStatusNo === VERIFY_STATUS_NO
  }
  get isDeleted() {
    return this.documentStatusNo === DELETED_STATUS_NO
  }
  get isVerify() {
    return this.docInfo.isVerify
  }
  get isVerifyRegistered() {
    return this.docInfo.isVerifyRegistered
  }
  get documentStatusNo() {
    return this.docInfo.documentStatusNo
  }
  abstract get ocrResp()
  abstract get ocrEditResult()
  abstract get config()

  get verifyData() {
    return this.docJSON.verifyData
  }

  async save(edit: { [x: string]: any }) {
    const document = merge(this.ocrEditResult, edit)
    return updateDocumentAPI(
      this.objectID,
      this.docInfo.documentStatusNo,
      document,
    )
  }

  async confirm(edit: { [x: string]: any }) {
    const doc = merge(this.ocrEditResult, edit)
    await confirmDocumentAPI(this.objectID, doc)
  }

  abstract verify(edit: { [x: string]: any })

  public canEditNormal() {
    if (this.isDeleted) return false
    if (
      this.isNew ||
      this.isEdited ||
      this.isSendError ||
      this.isGeneral ||
      (this.isSent && this.isOcrBuilder)
    )
      return true
    return false
  }
  canCsvDownloadable() {
    if (!this.isVerify) return true
    return checkVerifyCsvDownloadable(this.documentStatusNo)
  }

  canCheck() {
    if (!this.isVerify || this.isGeneral) return false
    return (
      (this.isNew || this.isChecking) &&
      !this.isVerifyRegistered &&
      !GLOBAL_PROFILE.isAdmin &&
      !GLOBAL_PROFILE.isManager
    )
  }

  canConfirm() {
    if (!this.isVerify || this.isGeneral) return false
    return this.isVerifying && GLOBAL_PROFILE.isManager
  }
  canSend() {
    return false
  }
  canEdit() {
    return this.isVerify ? this.canCheck() : this.canEditNormal()
  }
  public canSave() {
    // what is the difference?
    return this.canEdit()
  }

  abstract getHighlights(): Highlight[]

  getSrcs() {
    const { pageSize, objectID } = this.docInfo
    return [...Array(pageSize).keys()].map(
      (page) => `/api/v1/documents/${objectID}/image/${page}`,
    )
  }
}

export type Coordinate = [number, number]
export const COORDINATE_KEYS = [
  'top_left',
  'top_right',
  'bottom_right',
  'bottom_left',
] as const
export type Coordinates = {
  [key in (typeof COORDINATE_KEYS)[number]]: Coordinate
}

export type Highlight = {
  name: string
  label: string
  detectedPage: number
  coordinates: Coordinates
}
